/**
 * Feature Flags for Firebase Migration
 * Enables safe, gradual migration with instant rollback capability
 */

export interface FeatureFlags {
  // Phase 1: Low-risk read-only operations
  useFirestoreHooks: boolean;
  useCollectionCache: boolean;
  useFilterService: boolean;
  
  // Phase 2: Medium-risk write operations
  useStorageHooks: boolean;
  useFileUploadHooks: boolean;
  useContentManagement: boolean;
  
  // Phase 3: High-risk critical operations
  useUserManagement: boolean;
  useAddAudioForm: boolean;
  useAdminOperations: boolean;
  
  // Phase 4: Critical authentication (NEVER enable in production without thorough testing)
  useAuthHooks: boolean;
  useFunctionHooks: boolean;
  
  // Global emergency switch
  emergencyRollback: boolean;
}

// Default feature flags - VERY CONSERVATIVE for production safety
const DEFAULT_FLAGS: FeatureFlags = {
  // Phase 1: Start with read-only operations in development only
  useFirestoreHooks: process.env.NODE_ENV === 'development',
  useCollectionCache: true, // Enabled for Phase 1.1 migration
  useFilterService: true, // Enabled for Phase 1.2 migration
  
  // Phase 2: Medium-risk write operations
  useStorageHooks: true, // Enabled for Phase 2.1 migration
  useFileUploadHooks: false,
  useContentManagement: true, // Enabled for Phase 2.2 migration
  
  // Phase 3: High-risk critical operations
  useUserManagement: true, // Enabled for Phase 3.1 migration
  useAddAudioForm: true, // Enabled for Phase 3.2 migration
  useAdminOperations: false,
  
  // Phase 4: Critical authentication - EXTREME CAUTION
  useAuthHooks: process.env.NODE_ENV !== 'production', // Enabled for Phase 4 migration - NEVER in production
  useFunctionHooks: false,
  
  // Emergency switch
  emergencyRollback: false,
};

// Current feature flags (can be overridden)
let currentFlags: FeatureFlags = { ...DEFAULT_FLAGS };

// Check for emergency rollback on startup
if (typeof window !== 'undefined') {
  const emergencyRollback = localStorage.getItem('EMERGENCY_ROLLBACK');
  if (emergencyRollback === 'true') {
    // Emergency rollback - disable ALL migration features
    Object.keys(currentFlags).forEach(key => {
      if (key !== 'emergencyRollback') {
        (currentFlags as any)[key] = false;
      }
    });
    currentFlags.emergencyRollback = true;
    console.warn('🚨 EMERGENCY ROLLBACK ACTIVE - All Firebase hooks disabled');
  }
}

/**
 * Get current feature flag value
 */
export const getFeatureFlag = (flag: keyof FeatureFlags): boolean => {
  // Check for emergency rollback first
  if (currentFlags.emergencyRollback && flag !== 'emergencyRollback') {
    return false;
  }
  
  // Check localStorage override (for runtime flag changes)
  if (typeof window !== 'undefined') {
    const override = localStorage.getItem(`flag_${flag}`);
    if (override !== null) {
      return override === 'true';
    }
  }
  
  return currentFlags[flag];
};

/**
 * Set feature flag (admin only, with safety checks)
 * WARNING: Only use this for gradual rollout testing
 */
export const setFeatureFlag = (flag: keyof FeatureFlags, value: boolean, userRole?: string): boolean => {
  // Safety check - only admins can change flags
  if (userRole !== 'ADMIN') {
    console.warn('Feature flag changes require ADMIN role');
    return false;
  }
  
  // Extra safety for critical flags in production
  if (process.env.NODE_ENV === 'production') {
    const criticalFlags: (keyof FeatureFlags)[] = [
      'useAuthHooks',
      'useFunctionHooks',
      'useUserManagement',
      'useAdminOperations'
    ];
    
    if (criticalFlags.includes(flag) && value === true) {
      console.error(`🚨 Cannot enable critical flag '${flag}' in production without explicit override`);
      return false;
    }
  }
  
  // Update current flags
  currentFlags[flag] = value;
  
  // Store in localStorage for persistence
  if (typeof window !== 'undefined') {
    localStorage.setItem(`flag_${flag}`, value.toString());
  }
  
  console.log(`Feature flag '${flag}' set to: ${value}`);
  return true;
};

/**
 * Emergency rollback - disables ALL migration features
 * Can be called by any admin in case of production issues
 */
export const emergencyRollback = (userRole?: string): void => {
  if (userRole !== 'ADMIN') {
    console.warn('Emergency rollback requires ADMIN role');
    return;
  }
  
  console.error('🚨 EXECUTING EMERGENCY ROLLBACK - Disabling all Firebase hooks');
  
  // Disable all migration features
  Object.keys(currentFlags).forEach(key => {
    if (key !== 'emergencyRollback') {
      (currentFlags as any)[key] = false;
    }
  });
  
  currentFlags.emergencyRollback = true;
  
  // Store rollback state
  if (typeof window !== 'undefined') {
    localStorage.setItem('EMERGENCY_ROLLBACK', 'true');
    
    // Clear all individual flag overrides
    Object.keys(currentFlags).forEach(key => {
      localStorage.removeItem(`flag_${key}`);
    });
  }
  
  // Alert user
  alert('🚨 EMERGENCY ROLLBACK EXECUTED\n\nAll Firebase migration features have been disabled.\nPlease refresh the page to complete the rollback.');
};

/**
 * Clear emergency rollback state
 */
export const clearEmergencyRollback = (userRole?: string): void => {
  if (userRole !== 'ADMIN') {
    console.warn('Clear emergency rollback requires ADMIN role');
    return;
  }
  
  if (typeof window !== 'undefined') {
    localStorage.removeItem('EMERGENCY_ROLLBACK');
  }
  
  currentFlags.emergencyRollback = false;
  console.log('Emergency rollback state cleared');
};

/**
 * Get all current feature flags (for admin dashboard)
 */
export const getAllFeatureFlags = (): FeatureFlags => {
  const flags = { ...currentFlags };
  
  // Apply localStorage overrides
  if (typeof window !== 'undefined') {
    Object.keys(flags).forEach(key => {
      const override = localStorage.getItem(`flag_${key}`);
      if (override !== null) {
        (flags as any)[key] = override === 'true';
      }
    });
  }
  
  return flags;
};

/**
 * Reset all flags to default values
 */
export const resetFeatureFlags = (userRole?: string): void => {
  if (userRole !== 'ADMIN') {
    console.warn('Reset feature flags requires ADMIN role');
    return;
  }
  
  currentFlags = { ...DEFAULT_FLAGS };
  
  if (typeof window !== 'undefined') {
    // Clear all localStorage overrides
    Object.keys(DEFAULT_FLAGS).forEach(key => {
      localStorage.removeItem(`flag_${key}`);
    });
    localStorage.removeItem('EMERGENCY_ROLLBACK');
  }
  
  console.log('Feature flags reset to default values');
};

/**
 * Hook for using feature flags in components
 */
export const useFeatureFlag = (flag: keyof FeatureFlags): boolean => {
  return getFeatureFlag(flag);
};

/**
 * Migration helper - safely try hook method with fallback
 */
export const safeMigration = async <T>(
  flag: keyof FeatureFlags,
  hookMethod: () => Promise<T>,
  fallbackMethod: () => Promise<T>,
  componentName?: string
): Promise<T> => {
  if (!getFeatureFlag(flag)) {
    return fallbackMethod();
  }
  
  try {
    const result = await hookMethod();
    
    // Log success for monitoring
    if (componentName) {
      console.debug(`✅ Hook migration success: ${componentName}`);
    }
    
    return result;
  } catch (error) {
    console.error(`❌ Hook migration failed: ${componentName || flag}`, error);
    
    // Auto-disable flag on repeated failures
    if (componentName) {
      const failureKey = `migration_failures_${flag}`;
      const failures = parseInt(localStorage.getItem(failureKey) || '0') + 1;
      localStorage.setItem(failureKey, failures.toString());
      
      if (failures >= 3) {
        console.warn(`🚨 Auto-disabling ${flag} after ${failures} failures`);
        setFeatureFlag(flag, false, 'ADMIN'); // Auto-disable
      }
    }
    
    // Fall back to original method
    return fallbackMethod();
  }
};

// Export current flags for external access
export const FEATURE_FLAGS = currentFlags;

const featureFlagsDefault = {
  getFeatureFlag,
  setFeatureFlag,
  emergencyRollback,
  clearEmergencyRollback,
  getAllFeatureFlags,
  resetFeatureFlags,
  useFeatureFlag,
  safeMigration,
  FEATURE_FLAGS: currentFlags,
};

export default featureFlagsDefault;